import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { CollaborativeGroupWorkCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const StudentSkillsStep: React.FC<
  StepComponentProps<CollaborativeGroupWorkCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomMaterial, setShowCustomMaterial] = useState(false);

  const availableMaterialsOptions = [
    {
      id: "textbooks",
      emoji: "📚",
      label: t("collaborative.materials.textbooks"),
      desc: t("collaborative.materials.textbooksDesc"),
    },
    {
      id: "digital_devices",
      emoji: "💻",
      label: t("collaborative.materials.digitalDevices"),
      desc: t("collaborative.materials.digitalDevicesDesc"),
    },
    {
      id: "whiteboard",
      emoji: "⚪",
      label: t("collaborative.materials.whiteboard"),
      desc: t("collaborative.materials.whiteboardDesc"),
    },
    {
      id: "markers",
      emoji: "🖍️",
      label: t("collaborative.materials.markers"),
      desc: t("collaborative.materials.markersDesc"),
    },
    {
      id: "calculators",
      emoji: "🧮",
      label: t("collaborative.materials.calculators"),
      desc: t("collaborative.materials.calculatorsDesc"),
    },
  ];

  const studentSocialSkillsOptions = [
    {
      id: "beginner",
      emoji: "🌱",
      label: t("collaborative.socialSkills.beginner"),
      desc: t("collaborative.socialSkills.beginnerDesc"),
    },
    {
      id: "intermediate",
      emoji: "🌿",
      label: t("collaborative.socialSkills.intermediate"),
      desc: t("collaborative.socialSkills.intermediateDesc"),
    },
    {
      id: "advanced",
      emoji: "🌳",
      label: t("collaborative.socialSkills.advanced"),
      desc: t("collaborative.socialSkills.advancedDesc"),
    },
  ];

  const previousExperienceOptions = [
    {
      id: "none",
      emoji: "🆕",
      label: t("collaborative.experience.none"),
      desc: t("collaborative.experience.noneDesc"),
    },
    {
      id: "limited",
      emoji: "🔸",
      label: t("collaborative.experience.limited"),
      desc: t("collaborative.experience.limitedDesc"),
    },
    {
      id: "moderate",
      emoji: "🔶",
      label: t("collaborative.experience.moderate"),
      desc: t("collaborative.experience.moderateDesc"),
    },
    {
      id: "extensive",
      emoji: "🔥",
      label: t("collaborative.experience.extensive"),
      desc: t("collaborative.experience.extensiveDesc"),
    },
  ];

  const handleMaterialToggle = (materialId: string) => {
    const currentMaterials = data.availableMaterials || [];
    const isSelected = currentMaterials.includes(materialId);

    if (isSelected) {
      const newMaterials = currentMaterials.filter((id) => id !== materialId);
      onDataChange({ availableMaterials: newMaterials });
    } else {
      const newMaterials = [...currentMaterials, materialId];
      onDataChange({ availableMaterials: newMaterials });
    }
  };

  const handleCustomMaterialToggle = () => {
    const currentMaterials = data.availableMaterials || [];
    const hasCustomMaterial = currentMaterials.some(
      (material) =>
        !availableMaterialsOptions.some((option) => option.id === material),
    );

    if (hasCustomMaterial || showCustomMaterial) {
      const newMaterials = currentMaterials.filter((material) =>
        availableMaterialsOptions.some((option) => option.id === material),
      );
      onDataChange({ availableMaterials: newMaterials });
      setShowCustomMaterial(false);
    } else {
      setShowCustomMaterial(true);
    }
  };

  const handleCustomMaterialChange = (value: string) => {
    const currentMaterials = data.availableMaterials || [];
    const filteredMaterials = currentMaterials.filter((material) =>
      availableMaterialsOptions.some((option) => option.id === material),
    );

    if (value.trim()) {
      onDataChange({
        availableMaterials: [...filteredMaterials, value.trim()],
      });
    } else {
      onDataChange({ availableMaterials: filteredMaterials });
    }
  };

  const handleSocialSkillsSelect = (skill: string) => {
    onDataChange({ studentSocialSkills: skill });
  };

  const handleExperienceSelect = (experience: string) => {
    onDataChange({ previousCollaborationExperience: experience });
  };

  const isMaterialSelected = (materialId: string) => {
    return (data.availableMaterials || []).includes(materialId);
  };

  const isCustomMaterialSelected = () => {
    const currentMaterials = data.availableMaterials || [];
    return (
      showCustomMaterial ||
      currentMaterials.some(
        (material) =>
          !availableMaterialsOptions.some((option) => option.id === material),
      )
    );
  };

  const getCustomMaterialValue = () => {
    const currentMaterials = data.availableMaterials || [];
    const customMaterial = currentMaterials.find(
      (material) =>
        !availableMaterialsOptions.some((option) => option.id === material),
    );
    return customMaterial || "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("collaborative.steps.studentSkills.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("collaborative.steps.studentSkills.description")}
        </p>
      </div>

      {/* Available Materials */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("collaborative.form.availableMaterials")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("collaborative.form.availableMaterialsDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {availableMaterialsOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isMaterialSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleMaterialToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isMaterialSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isMaterialSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Material Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomMaterialSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomMaterialToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isCustomMaterialSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomMaterialSelected() && (
                  <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("collaborative.materials.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("collaborative.materials.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Material Input */}
        {isCustomMaterialSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("collaborative.form.customMaterial")}
            </label>
            <input
              type="text"
              value={getCustomMaterialValue()}
              onChange={(e) => handleCustomMaterialChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("collaborative.form.customMaterialPlaceholder")}
            />
          </div>
        )}
      </div>

      {/* Student Social Skills */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("collaborative.form.studentSocialSkills")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {studentSocialSkillsOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.studentSocialSkills === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleSocialSkillsSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.studentSocialSkills === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.studentSocialSkills === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Previous Collaboration Experience */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("collaborative.form.previousExperience")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {previousExperienceOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.previousCollaborationExperience === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleExperienceSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.previousCollaborationExperience === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.previousCollaborationExperience === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
